import { useEffect, useRef, MutableRefObject } from 'react';

interface Options {
  // When false, the hook is a no-op (used to let callers fall back to plain
  // 'ended' listeners for bypass users like creators/admins).
  enabled?: boolean;
  // Resetting key: when this changes (e.g., lesson.id), tracking state resets.
  resetKey?: string | number;
  // Minimum fraction of duration the learner must spend in `playing` state
  // (wall-clock) before onComplete fires. Defaults to 0.6 — generous
  // enough that the natural "I watched it through" path always fires at
  // the `ended` event, strict enough to reject a scrub-to-end attempt
  // (which spends ~0 seconds in playing state).
  threshold?: number;
  // Fires once when the threshold is reached, or at the `ended` event
  // when the cumulative play time covers a meaningful slice of the video.
  onComplete: () => void;
}

/**
 * Tracks how much wall-clock time a learner has spent with the video in
 * `playing` state — and fires `onComplete` once that crosses
 * `threshold * duration`. This is more robust than counting `timeupdate`
 * events: Mux-player and other custom-element video players don't
 * always relay `timeupdate` to listeners attached to the host element,
 * which left the previous unique-seconds and delta-based implementations
 * stuck at zero even when the learner watched the entire video through.
 *
 * Scrubbing-to-end can't game this: a seek raises currentTime but the
 * element stays paused, so no play-time accrues. Pressing play near the
 * end and letting the last second tick still won't cross the threshold
 * on a normal-length video.
 */
export function useVideoWatchTracking(
  playerRef: MutableRefObject<any>,
  options: Options
) {
  const { enabled = true, resetKey, threshold = 0.6, onComplete } = options;
  // Cumulative wall-clock milliseconds the player has been in playing state
  // since the lesson loaded.
  const playedMsRef = useRef(0);
  // Timestamp of the most recent `play` event — null when paused/ended.
  const playStartRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  // Keep the completion callback in a ref so the listener effect below
  // doesn't list it as a dependency. Callers pass an inline closure
  // (`() => onVideoEnded?.()`), which is a fresh function every render —
  // and the player now re-renders ~5×/sec while the seek guard updates
  // its progress state. If that closure were a dependency, the effect
  // would tear down and re-attach on every render; the teardown's
  // `flush()` nulls `playStartRef`, and since no fresh `play` event
  // fires during continuous playback the play-clock would never restart,
  // freezing watch time below the threshold so completion never fires.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Reset tracking when the underlying lesson/media changes.
  useEffect(() => {
    playedMsRef.current = 0;
    playStartRef.current = null;
    firedRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (!enabled) return;
    const player = playerRef.current;
    if (!player) return;

    // Flush the open play-segment into the cumulative total.
    const flush = () => {
      if (playStartRef.current != null) {
        playedMsRef.current += Date.now() - playStartRef.current;
        playStartRef.current = null;
      }
    };

    const fireIfReady = () => {
      if (firedRef.current) return;
      const duration = Number(player.duration);
      if (!duration || !isFinite(duration) || duration <= 0) return;
      const ratio = playedMsRef.current / 1000 / duration;
      if (ratio >= threshold) {
        firedRef.current = true;
        onCompleteRef.current();
      }
    };

    const handlePlay = () => {
      if (playStartRef.current == null) playStartRef.current = Date.now();
    };
    const handlePause = () => {
      flush();
      fireIfReady();
    };
    const handleEnded = () => {
      flush();
      fireIfReady();
    };
    // Some players dispatch `playing` instead of `play` (e.g., after a
    // buffer / seek). Treat both as "the clock is running."
    const handlePlaying = () => {
      if (playStartRef.current == null) playStartRef.current = Date.now();
    };

    player.addEventListener('play', handlePlay);
    player.addEventListener('playing', handlePlaying);
    player.addEventListener('pause', handlePause);
    player.addEventListener('ended', handleEnded);

    // If the effect (re)attaches while the media is already playing —
    // e.g., a resetKey change mid-playback — restart the clock so no
    // watch time is dropped between teardown and re-attach.
    if (player.paused === false) handlePlay();

    return () => {
      // Flush one last time on unmount so the cumulative total isn't
      // lost when the listeners detach (e.g., the player remounts on
      // lesson change).
      flush();
      player.removeEventListener('play', handlePlay);
      player.removeEventListener('playing', handlePlaying);
      player.removeEventListener('pause', handlePause);
      player.removeEventListener('ended', handleEnded);
    };
  }, [playerRef, threshold, resetKey, enabled]);
}
